import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { runCashflowForecast } from '../../api';
import EmptyBanner from './components/EmptyBanner';
import PageHeader from './components/PageHeader';
import AppHeader from '../../components/AppHeader';
import PredictionToolbar from './components/PredictionToolbar';
import ScenarioSection from './components/ScenarioSection';
import SummaryCards from './components/SummaryCards';
import {
  CASHFLOW_FORECAST_DASHBOARD_MOCK,
  createCashflowForecastDashboardResponse,
} from './mock';
import type {
  CashflowForecastMockDefinition,
  CashflowPredictionSnapshot,
  ForecastScenarioKey,
} from './types';

const DEFAULT_QUICK_RANGE_DAYS = 180;
const MIN_RANGE_DAYS = 7;
const MAX_RANGE_DAYS = 180;

const CashflowForecastDashboardPage: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [dashboardDefinition, setDashboardDefinition] =
    useState<CashflowForecastMockDefinition>(CASHFLOW_FORECAST_DASHBOARD_MOCK);
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(() =>
    dayjs().add(DEFAULT_QUICK_RANGE_DAYS, 'day').startOf('day')
  );
  const [selectedQuickRangeDays, setSelectedQuickRangeDays] = useState<
    number | undefined
  >(DEFAULT_QUICK_RANGE_DAYS);
  const [draftEndDate, setDraftEndDate] = useState<Dayjs>(() =>
    dayjs().add(DEFAULT_QUICK_RANGE_DAYS, 'day').startOf('day')
  );
  const [draftQuickRangeDays, setDraftQuickRangeDays] = useState<
    number | undefined
  >(DEFAULT_QUICK_RANGE_DAYS);
  const [panelOpen, setPanelOpen] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [lastPredictedAt, setLastPredictedAt] = useState<string>();
  const [headerDataUpdatedAt, setHeaderDataUpdatedAt] = useState('——');
  const [activeScenario, setActiveScenario] =
    useState<ForecastScenarioKey>('base');
  const [prediction, setPrediction] = useState<CashflowPredictionSnapshot>();

  const dateDifferenceDays = useMemo(() => {
    return Math.max(
      draftEndDate.startOf('day').diff(dayjs().startOf('day'), 'day'),
      1
    );
  }, [draftEndDate]);

  const displayRangeEndText = prediction
    ? selectedEndDate.format('YYYY-MM-DD')
    : '——';
  const startDateText = dayjs().format('YYYY-MM-DD');

  const handleQuickRangeChange = (days: number) => {
    setDraftQuickRangeDays(days);
    setDraftEndDate(dayjs().add(days, 'day').startOf('day'));
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    if (!value) return;

    const minDate = dayjs().add(MIN_RANGE_DAYS, 'day').startOf('day');
    const maxDate = dayjs().add(MAX_RANGE_DAYS, 'day').startOf('day');
    let nextDate = value.startOf('day');

    if (nextDate.isBefore(minDate)) {
      nextDate = minDate;
    } else if (nextDate.isAfter(maxDate)) {
      nextDate = maxDate;
    }

    setDraftEndDate(nextDate);
    setDraftQuickRangeDays(undefined);
  };

  const handleTogglePanel = () => {
    if (predicting) return;

    if (panelOpen) {
      setDraftEndDate(selectedEndDate);
      setDraftQuickRangeDays(selectedQuickRangeDays);
      setPanelOpen(false);
      return;
    }

    setDraftEndDate(selectedEndDate);
    setDraftQuickRangeDays(selectedQuickRangeDays);
    setPanelOpen(true);
  };

  const handleCancel = () => {
    setDraftEndDate(selectedEndDate);
    setDraftQuickRangeDays(selectedQuickRangeDays);
    setPanelOpen(false);
  };

  const handleConfirm = () => {
    if (predicting) return;

    const confirmedEndDate = draftEndDate;
    const confirmedQuickRangeDays = draftQuickRangeDays;

    setSelectedEndDate(confirmedEndDate);
    setSelectedQuickRangeDays(confirmedQuickRangeDays);
    setPanelOpen(false);
    setPredicting(true);

    void runCashflowForecast({
      predictionEndDate: confirmedEndDate.format('YYYY-MM-DD'),
    })
      .then(response => {
        const mappedResponse = createCashflowForecastDashboardResponse(
          response,
          confirmedEndDate.format('YYYY-MM-DD'),
          dashboardDefinition
        );
        const generatedAt =
          mappedResponse.generatedAt || dayjs().format('YYYY-MM-DD HH:mm');

        setDashboardDefinition(current => ({
          ...current,
          title: mappedResponse.title || current.title,
          quickRangeDays: mappedResponse.quickRangeDays?.length
            ? mappedResponse.quickRangeDays
            : current.quickRangeDays,
          riskConfig: mappedResponse.riskConfig || current.riskConfig,
          scenarioDefinitions: mappedResponse.scenarioDefinitions?.length
            ? mappedResponse.scenarioDefinitions
            : current.scenarioDefinitions,
          branchBalances: mappedResponse.branchBalances?.length
            ? mappedResponse.branchBalances
            : current.branchBalances,
        }));
        setPrediction(mappedResponse);
        setLastPredictedAt(generatedAt);
        setHeaderDataUpdatedAt(generatedAt);
        setActiveScenario(current => {
          const nextScenarioDefinitions = mappedResponse.scenarioDefinitions?.length
            ? mappedResponse.scenarioDefinitions
            : dashboardDefinition.scenarioDefinitions;
          return nextScenarioDefinitions.some(item => item.key === current)
            ? current
            : nextScenarioDefinitions[0]?.key || 'base';
        });
      })
      .catch((error: Error) => {
        messageApi.error(error.message || '现金流预测请求失败，请稍后重试');
      })
      .finally(() => {
        setPredicting(false);
      });
  };

  return (
    <div className="h-full min-h-full overflow-auto bg-[#f3f4f6]">
      <AppHeader />
      {contextHolder}
      <div className="mx-auto flex min-h-full w-full max-w-[1480px] flex-col p-[14px]">
        <div className="rounded-[12px] bg-white px-5 py-4 shadow-[0_4px_16px_rgba(15,23,42,0.06)]">
          <PageHeader
            title={dashboardDefinition.title}
            subtitle={dashboardDefinition.subtitle}
            dataUpdatedAt={headerDataUpdatedAt}
          />

          <PredictionToolbar
            displayRangeEndText={displayRangeEndText}
            quickRangeDays={dashboardDefinition.quickRangeDays}
            panelOpen={panelOpen}
            predicting={predicting}
            panelSelectionDate={draftEndDate}
            panelSelectionDays={dateDifferenceDays}
            panelSelectionMode={draftQuickRangeDays ? 'preset' : 'custom'}
            startDateText={startDateText}
            lastPredictedAt={lastPredictedAt}
            onTogglePanel={handleTogglePanel}
            onQuickRangeChange={handleQuickRangeChange}
            onEndDateChange={handleEndDateChange}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />

          {!prediction ? <EmptyBanner /> : null}

          <SummaryCards
            branchBalances={dashboardDefinition.branchBalances}
            prediction={prediction}
            riskConfig={dashboardDefinition.riskConfig}
          />

          <ScenarioSection
            prediction={prediction}
            scenarioDefinitions={dashboardDefinition.scenarioDefinitions}
            activeScenario={activeScenario}
            onScenarioChange={setActiveScenario}
          />
        </div>
      </div>
    </div>
  );
};

export default CashflowForecastDashboardPage;
